import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, Users, UserCircle, Calendar, Trophy, BarChart3,
  Award, Settings, CreditCard, LogOut, Menu, X, Shield, ClipboardList, FileText, MessageSquare, TrendingUp, Image as ImageIcon
} from 'lucide-react';
import { useState } from 'react';

const navItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/tournament-info', labelKey: 'nav.tournamentInfo', icon: FileText },
  { path: '/standings', labelKey: 'nav.standings', icon: Trophy },
  { path: '/matches', labelKey: 'nav.matches', icon: Calendar },
  { path: '/teams', labelKey: 'nav.teams', icon: Users },
  { path: '/players', labelKey: 'nav.players', icon: UserCircle },
  { path: '/stats', labelKey: 'nav.playerStats', icon: BarChart3 },
  { path: '/sponsors', labelKey: 'nav.sponsors', icon: Award },
  { path: '/referees', labelKey: 'nav.referees', icon: Shield },
  { path: '/referee-assignments', labelKey: 'nav.refAssignments', icon: ClipboardList },
  { path: '/blog', labelKey: 'nav.blog', icon: MessageSquare },
  { path: '/gallery', labelKey: 'Gallery', icon: ImageIcon },
  { path: '/analytics', labelKey: 'Analytics', icon: TrendingUp },
];

const bottomNavItems = [
  { path: '/settings', labelKey: 'nav.settings', icon: Settings },
  { path: '/billing', labelKey: 'nav.billing', icon: CreditCard },
];

export default function DashboardLayout({ children }) {
  const { user, organization, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        data-testid={`nav-${t(item.labelKey).toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 text-sm transition-all ${
          isActive 
            ? 'bg-emerald-500 text-white rounded-lg mx-2' 
            : 'text-gray-700 hover:text-black hover:bg-gray-100 mx-2 rounded-lg'
        }`}
      >
        <Icon size={20} strokeWidth={1.5} />
        <span>{t(item.labelKey)}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-gray-200 bg-gray-50 fixed h-full">
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src="/logo.jpg" 
              alt="Score Copilot" 
              className="h-10 w-auto object-contain"
            />
            <span className="font-heading text-lg font-bold text-black uppercase">
              Score Copilot
            </span>
          </Link>
          <div className="mt-2">
            <p className="text-sm text-emerald-600 font-medium">
              {organization?.name || 'My League'}
            </p>
            <p className="text-xs text-gray-500">
              {organization?.tournament_name || 'Tournament'}
            </p>
          </div>
        </div>

        {/* Main Nav */}
        <nav className="flex-1 py-4 space-y-0.5">
          {navItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
        </nav>

        {/* Bottom Nav */}
        <div className="border-t border-gray-200 py-4 space-y-0.5">
          {bottomNavItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}
          <button
            onClick={handleLogout}
            data-testid="nav-logout"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 mx-2 rounded-lg transition-all"
          >
            <LogOut size={20} strokeWidth={1.5} />
            <span>{t('nav.logout')}</span>
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-emerald-600 text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-black truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2">
            <img 
              src="/logo.jpg" 
              alt="Score Copilot" 
              className="h-8 w-auto object-contain"
            />
            <span className="font-heading text-lg font-bold text-black uppercase">
              Score Copilot
            </span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-gray-600 hover:text-black"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-white border-b border-gray-200 py-4">
            {navItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
            <div className="border-t border-gray-200 mt-4 pt-4">
              {bottomNavItems.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-500 hover:text-red-600"
              >
                <LogOut size={20} strokeWidth={1.5} />
                <span>{t('nav.logout')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
